import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type {
  AppRolesResponse,
  WapsignRole,
  WapmeetRole,
  WapstatRole,
  WapstatZoneRole,
  WapproRole,
  WapdocsRole,
  AppRoleUpdateStatus,
  AppProvisionStatus,
  UpdateAppRolesBody,
  ProvisionAppBody,
  UpdateAppRolesResult,
} from './api.ts';

// Minimal shape the chips/editor need from a user row. Both the sys-admin
// OrgUserRow and the org-admin OrgUserRow satisfy this.
export interface AppRoleUser {
  id: string;
  email: string;
}

const WAPMEET_ROLES: Record<string, string> = {
  PRESIDENT: 'President',
  VICE_PRESIDENT: 'Vice President',
  SECRETARY_GENERAL: 'Secretary General',
  MEMBER: 'Member',
  BUREAU_STAFF: 'Bureau Staff',
  VIEWER: 'Viewer',
  WDC_PRESIDENT: 'WDC President',
  WDC_VICE_PRESIDENT: 'WDC Vice President',
  WDC_MEMBER: 'WDC Member',
  SYS_ADMIN: 'Sys admin',
};

const WAPSTAT_ROLES: Record<string, string> = {
  ADMIN: 'Admin',
  ENUMERATOR: 'Enumerator',
};

const WAPSTAT_ZONE_ROLES: Record<string, string> = {
  COUNCILLOR: 'Councillor',
  SECRETARY_GENERAL: 'Secretary General',
  STAFF: 'Staff',
};

const WAPPRO_ROLES: Record<string, string> = {
  ADMIN: 'Admin',
  STAFF: 'Procurement Staff',
  STOCKKEEPER: 'Stockkeeper',
  FINANCE: 'Finance',
};

// Per-app role chips — roles are independent in each app (someone can be an
// admin in WapSign but a plain member in WapMeet).
export function AppRoleChips({
  email,
  roles,
}: {
  email: string;
  roles?: AppRolesResponse;
}): JSX.Element | null {
  if (!roles) return null;
  // Defensive: tolerate a missing per-app key so an incomplete response can
  // never hard-crash the whole page ("Cannot read properties of undefined").
  const ws = roles.wapsign?.users.find((u) => u.email === email);
  const wm = roles.wapmeet?.users.find((u) => u.email === email);
  const wst = roles.wapstat?.users.find((u) => u.email === email);
  const wq = roles.wappro?.users.find((u) => u.email === email);
  const wd = roles.wapdocs?.users.find((u) => u.email === email);
  const chip =
    'inline-flex items-center gap-1 rounded-lg bg-[hsl(var(--surface-container-highest))] px-2 py-0.5 text-[11px]';
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      <span className={chip}>
        <span className="text-muted-foreground">WapSign:</span>
        {ws ? (
          <span className={ws.isActive ? '' : 'line-through text-muted-foreground'}>
            {ws.role === 'ORG_ADMIN' ? 'Org admin' : ws.role === 'SYS_ADMIN' ? 'Sys admin' : 'User'}
          </span>
        ) : (
          <span className="text-muted-foreground">no account</span>
        )}
      </span>
      <span className={chip}>
        <span className="text-muted-foreground">WapMeet:</span>
        {wm ? (
          <span className={wm.isActive ? '' : 'line-through text-muted-foreground'}>
            {(wm.role && WAPMEET_ROLES[wm.role]) ?? wm.role}
            {wm.isOrgAdmin ? ' · Admin' : ''}
          </span>
        ) : (
          <span className="text-muted-foreground">no account</span>
        )}
      </span>
      <span className={chip}>
        <span className="text-muted-foreground">WapStat:</span>
        {wst ? (
          <span className={wst.isActive ? '' : 'line-through text-muted-foreground'}>
            {[
              wst.role ? (WAPSTAT_ROLES[wst.role] ?? wst.role) : null,
              wst.zoneRole ? `Zone ${WAPSTAT_ZONE_ROLES[wst.zoneRole] ?? wst.zoneRole}` : null,
              wst.isOrgAdmin ? 'Admin' : null,
            ]
              .filter(Boolean)
              .join(' · ') || 'No role'}
          </span>
        ) : (
          <span className="text-muted-foreground">no account</span>
        )}
      </span>
      <span className={chip}>
        <span className="text-muted-foreground">WapPro:</span>
        {wq ? (
          <span className={wq.isActive ? '' : 'line-through text-muted-foreground'}>
            {(wq.role && WAPPRO_ROLES[wq.role]) ?? wq.role}
          </span>
        ) : (
          <span className="text-muted-foreground">no account</span>
        )}
      </span>
      <span className={chip}>
        <span className="text-muted-foreground">WapDocs:</span>
        {wd ? (
          <span className={wd.isActive ? '' : 'line-through text-muted-foreground'}>
            {wd.role === 'ORG_ADMIN' ? 'Org admin' : wd.role === 'SYS_ADMIN' ? 'Sys admin' : 'User'}
          </span>
        ) : (
          <span className="text-muted-foreground">no account</span>
        )}
      </span>
    </div>
  );
}

const WAPMEET_ROLE_OPTIONS: { value: WapmeetRole; label: string }[] = [
  { value: 'PRESIDENT', label: 'President' },
  { value: 'VICE_PRESIDENT', label: 'Vice President' },
  { value: 'MEMBER', label: 'Member' },
  { value: 'SECRETARY_GENERAL', label: 'Secretary General' },
  { value: 'BUREAU_STAFF', label: 'Bureau Staff' },
  { value: 'VIEWER', label: 'Viewer' },
  { value: 'WDC_PRESIDENT', label: 'WDC President' },
  { value: 'WDC_VICE_PRESIDENT', label: 'WDC Vice President' },
  { value: 'WDC_MEMBER', label: 'WDC Member' },
];

const WAPSTAT_ROLE_OPTIONS: { value: WapstatRole; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'ENUMERATOR', label: 'Enumerator' },
];

const WAPSTAT_ZONE_ROLE_OPTIONS: { value: WapstatZoneRole; label: string }[] = [
  { value: 'COUNCILLOR', label: 'Councillor' },
  { value: 'SECRETARY_GENERAL', label: 'Secretary General' },
  { value: 'STAFF', label: 'Staff' },
];

const WAPSTAT_NO_ROLE_ERROR = 'Give at least one WapStat role';

const WAPPRO_ROLE_OPTIONS: { value: WapproRole; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'STAFF', label: 'Procurement Staff' },
  { value: 'STOCKKEEPER', label: 'Stockkeeper' },
  { value: 'FINANCE', label: 'Finance' },
];

const updateRoleError = (s: AppRoleUpdateStatus): string =>
  s === 'not_found'
    ? 'no account'
    : s === 'forbidden'
      ? 'blocked: sys admin account'
      : 'failed — try again';

const provisionMsg = (s: AppProvisionStatus): string =>
  s === 'ok'
    ? 'Account created.'
    : s === 'exists'
      ? 'Account already existed.'
      : 'failed — try again';

// Inline per-app role editor for one user. Shows a row per app: change the role
// of an existing account, or create one for an existing office user. The host
// supplies the API calls (org-scoped for sys-admin, caller's-own-org for the
// org admin) plus an `onDone` to refresh after a successful change.
export function AppRoleEditor({
  user,
  roles,
  hasWapsign,
  hasWapmeet,
  hasWapstat,
  hasWappro,
  hasWapdocs,
  updateAppRoles,
  provisionApp,
  onDone,
}: {
  user: AppRoleUser;
  roles?: AppRolesResponse;
  hasWapsign: boolean;
  hasWapmeet: boolean;
  hasWapstat: boolean;
  hasWappro: boolean;
  hasWapdocs: boolean;
  updateAppRoles: (userId: string, body: UpdateAppRolesBody) => Promise<UpdateAppRolesResult>;
  provisionApp: (userId: string, body: ProvisionAppBody) => Promise<AppProvisionStatus>;
  onDone: () => void;
}): JSX.Element {
  const ws = roles?.wapsign?.users.find((u) => u.email === user.email);
  const wm = roles?.wapmeet?.users.find((u) => u.email === user.email);
  const wst = roles?.wapstat?.users.find((u) => u.email === user.email);
  const wq = roles?.wappro?.users.find((u) => u.email === user.email);
  const wd = roles?.wapdocs?.users.find((u) => u.email === user.email);

  const [wsRole, setWsRole] = useState<WapsignRole>(
    ws?.role === 'ORG_ADMIN' ? 'ORG_ADMIN' : 'USER',
  );
  const [wmRole, setWmRole] = useState<WapmeetRole>(
    wm && WAPMEET_ROLE_OPTIONS.some((o) => o.value === wm.role)
      ? (wm.role as WapmeetRole)
      : 'MEMBER',
  );
  const [wmAdmin, setWmAdmin] = useState<boolean>(!!wm?.isOrgAdmin);
  // Census role can be None (zone-only user); a brand-new account defaults to Enumerator.
  const [wstRole, setWstRole] = useState<WapstatRole | null>(
    wst
      ? WAPSTAT_ROLE_OPTIONS.some((o) => o.value === wst.role)
        ? (wst.role as WapstatRole)
        : null
      : 'ENUMERATOR',
  );
  const [wstZoneRole, setWstZoneRole] = useState<WapstatZoneRole | null>(
    wst && WAPSTAT_ZONE_ROLE_OPTIONS.some((o) => o.value === wst.zoneRole)
      ? (wst.zoneRole as WapstatZoneRole)
      : null,
  );
  const [wstAdmin, setWstAdmin] = useState<boolean>(!!wst?.isOrgAdmin);
  const [wqRole, setWqRole] = useState<WapproRole>(
    wq && WAPPRO_ROLE_OPTIONS.some((o) => o.value === wq.role)
      ? (wq.role as WapproRole)
      : 'STAFF',
  );
  const [wdRole, setWdRole] = useState<WapdocsRole>(
    wd?.role === 'ORG_ADMIN' ? 'ORG_ADMIN' : 'USER',
  );
  const [msg, setMsg] = useState<Record<'wapsign' | 'wapmeet' | 'wapstat' | 'wappro' | 'wapdocs', string | null>>({
    wapsign: null,
    wapmeet: null,
    wapstat: null,
    wappro: null,
    wapdocs: null,
  });

  // WapStat refuses an account with no census role, no zone role and no org-admin flag.
  const wstNoAccess = !wstRole && !wstZoneRole && !wstAdmin;

  const selectCls = 'select w-auto py-1.5 text-xs';
  const applyBtn = 'btn-secondary btn-sm';

  const setAppMsg = (app: 'wapsign' | 'wapmeet' | 'wapstat' | 'wappro' | 'wapdocs', m: string | null) =>
    setMsg((s) => ({ ...s, [app]: m }));

  const applyWs = useMutation({
    mutationFn: () => updateAppRoles(user.id, { wapsign: { role: wsRole } }),
    onSuccess: (r) => {
      setAppMsg('wapsign', r.wapsign === 'ok' ? 'Updated.' : updateRoleError(r.wapsign ?? 'failed'));
      if (r.wapsign === 'ok') onDone();
    },
    onError: () => setAppMsg('wapsign', 'failed — try again'),
  });
  const createWs = useMutation({
    mutationFn: () => provisionApp(user.id, { app: 'wapsign', role: wsRole }),
    onSuccess: (r) => {
      setAppMsg('wapsign', provisionMsg(r));
      if (r !== 'failed') onDone();
    },
    onError: () => setAppMsg('wapsign', 'failed — try again'),
  });
  const applyWm = useMutation({
    mutationFn: () => updateAppRoles(user.id, { wapmeet: { role: wmRole, isOrgAdmin: wmAdmin } }),
    onSuccess: (r) => {
      setAppMsg('wapmeet', r.wapmeet === 'ok' ? 'Updated.' : updateRoleError(r.wapmeet ?? 'failed'));
      if (r.wapmeet === 'ok') onDone();
    },
    onError: () => setAppMsg('wapmeet', 'failed — try again'),
  });
  const createWm = useMutation({
    mutationFn: () => provisionApp(user.id, { app: 'wapmeet', role: wmRole, isOrgAdmin: wmAdmin }),
    onSuccess: (r) => {
      setAppMsg('wapmeet', provisionMsg(r));
      if (r !== 'failed') onDone();
    },
    onError: () => setAppMsg('wapmeet', 'failed — try again'),
  });
  const applyWst = useMutation({
    mutationFn: () => updateAppRoles(user.id, { wapstat: { role: wstRole, zoneRole: wstZoneRole, isOrgAdmin: wstAdmin } }),
    onSuccess: (r) => {
      setAppMsg('wapstat', r.wapstat === 'ok' ? 'Updated.' : updateRoleError(r.wapstat ?? 'failed'));
      if (r.wapstat === 'ok') onDone();
    },
    onError: () => setAppMsg('wapstat', 'failed — try again'),
  });
  const createWst = useMutation({
    mutationFn: () => provisionApp(user.id, { app: 'wapstat', role: wstRole, zoneRole: wstZoneRole, isOrgAdmin: wstAdmin }),
    onSuccess: (r) => {
      setAppMsg('wapstat', provisionMsg(r));
      if (r !== 'failed') onDone();
    },
    onError: () => setAppMsg('wapstat', 'failed — try again'),
  });
  const applyWq = useMutation({
    mutationFn: () => updateAppRoles(user.id, { wappro: { role: wqRole } }),
    onSuccess: (r) => {
      setAppMsg('wappro', r.wappro === 'ok' ? 'Updated.' : updateRoleError(r.wappro ?? 'failed'));
      if (r.wappro === 'ok') onDone();
    },
    onError: () => setAppMsg('wappro', 'failed — try again'),
  });
  const createWq = useMutation({
    mutationFn: () => provisionApp(user.id, { app: 'wappro', role: wqRole }),
    onSuccess: (r) => {
      setAppMsg('wappro', provisionMsg(r));
      if (r !== 'failed') onDone();
    },
    onError: () => setAppMsg('wappro', 'failed — try again'),
  });
  const applyWd = useMutation({
    mutationFn: () => updateAppRoles(user.id, { wapdocs: { role: wdRole } }),
    onSuccess: (r) => {
      setAppMsg('wapdocs', r.wapdocs === 'ok' ? 'Updated.' : updateRoleError(r.wapdocs ?? 'failed'));
      if (r.wapdocs === 'ok') onDone();
    },
    onError: () => setAppMsg('wapdocs', 'failed — try again'),
  });
  const createWd = useMutation({
    mutationFn: () => provisionApp(user.id, { app: 'wapdocs', role: wdRole }),
    onSuccess: (r) => {
      setAppMsg('wapdocs', provisionMsg(r));
      if (r !== 'failed') onDone();
    },
    onError: () => setAppMsg('wapdocs', 'failed — try again'),
  });

  return (
    <div className="mt-2 rounded-xl bg-[hsl(var(--surface-container))] p-4 space-y-3 text-sm">
      {/* WapSign */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-20 text-xs font-medium text-muted-foreground">WapSign</span>
          <select
            aria-label="WapSign role"
            value={wsRole}
            onChange={(e) => setWsRole(e.target.value as WapsignRole)}
            className={selectCls}
          >
            <option value="USER">User</option>
            <option value="ORG_ADMIN">Org admin</option>
          </select>
          {ws ? (
            <button
              type="button"
              className={applyBtn}
              disabled={applyWs.isPending}
              onClick={() => {
                setAppMsg('wapsign', null);
                applyWs.mutate();
              }}
            >
              {applyWs.isPending ? 'Applying…' : 'Apply'}
            </button>
          ) : hasWapsign ? (
            <button
              type="button"
              className={applyBtn}
              disabled={createWs.isPending}
              onClick={() => {
                setAppMsg('wapsign', null);
                createWs.mutate();
              }}
            >
              {createWs.isPending ? 'Creating…' : 'Create account'}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">(org not linked)</span>
          )}
          {msg.wapsign && <span className="text-xs text-muted-foreground">{msg.wapsign}</span>}
        </div>
      </div>

      {/* WapMeet */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-20 text-xs font-medium text-muted-foreground">WapMeet</span>
          <select
            aria-label="WapMeet role"
            value={wmRole}
            onChange={(e) => setWmRole(e.target.value as WapmeetRole)}
            className={selectCls}
          >
            {WAPMEET_ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" className="size-4 shrink-0 rounded accent-primary" checked={wmAdmin} onChange={(e) => setWmAdmin(e.target.checked)} />
            Org admin
          </label>
          {wm ? (
            <button
              type="button"
              className={applyBtn}
              disabled={applyWm.isPending}
              onClick={() => {
                setAppMsg('wapmeet', null);
                applyWm.mutate();
              }}
            >
              {applyWm.isPending ? 'Applying…' : 'Apply'}
            </button>
          ) : hasWapmeet ? (
            <button
              type="button"
              className={applyBtn}
              disabled={createWm.isPending}
              onClick={() => {
                setAppMsg('wapmeet', null);
                createWm.mutate();
              }}
            >
              {createWm.isPending ? 'Creating…' : 'Create account'}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">(org not linked)</span>
          )}
          {msg.wapmeet && <span className="text-xs text-muted-foreground">{msg.wapmeet}</span>}
        </div>
      </div>

      {/* WapStat */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-20 text-xs font-medium text-muted-foreground">WapStat</span>
          <select
            aria-label="WapStat census role"
            value={wstRole ?? ''}
            onChange={(e) => setWstRole((e.target.value || null) as WapstatRole | null)}
            className={selectCls}
          >
            <option value="">Census: None</option>
            {WAPSTAT_ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                Census: {o.label}
              </option>
            ))}
          </select>
          <select
            aria-label="WapStat zone role"
            value={wstZoneRole ?? ''}
            onChange={(e) => setWstZoneRole((e.target.value || null) as WapstatZoneRole | null)}
            className={selectCls}
          >
            <option value="">Zone: None</option>
            {WAPSTAT_ZONE_ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                Zone: {o.label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className="size-4 shrink-0 rounded accent-primary"
              checked={wstAdmin}
              onChange={(e) => setWstAdmin(e.target.checked)}
            />
            Org admin
          </label>
          {wst ? (
            <button
              type="button"
              className={applyBtn}
              disabled={applyWst.isPending || wstNoAccess}
              onClick={() => {
                setAppMsg('wapstat', null);
                applyWst.mutate();
              }}
            >
              {applyWst.isPending ? 'Applying…' : 'Apply'}
            </button>
          ) : hasWapstat ? (
            <button
              type="button"
              className={applyBtn}
              disabled={createWst.isPending || wstNoAccess}
              onClick={() => {
                setAppMsg('wapstat', null);
                createWst.mutate();
              }}
            >
              {createWst.isPending ? 'Creating…' : 'Create account'}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">(org not linked)</span>
          )}
          {wstNoAccess && (
            <span role="alert" className="text-xs text-destructive">
              {WAPSTAT_NO_ROLE_ERROR}
            </span>
          )}
          {msg.wapstat && <span className="text-xs text-muted-foreground">{msg.wapstat}</span>}
        </div>
      </div>

      {/* WapPro */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-20 text-xs font-medium text-muted-foreground">WapPro</span>
          <select
            aria-label="WapPro role"
            value={wqRole}
            onChange={(e) => setWqRole(e.target.value as WapproRole)}
            className={selectCls}
          >
            {WAPPRO_ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {wq ? (
            <button
              type="button"
              className={applyBtn}
              disabled={applyWq.isPending}
              onClick={() => {
                setAppMsg('wappro', null);
                applyWq.mutate();
              }}
            >
              {applyWq.isPending ? 'Applying…' : 'Apply'}
            </button>
          ) : hasWappro ? (
            <button
              type="button"
              className={applyBtn}
              disabled={createWq.isPending}
              onClick={() => {
                setAppMsg('wappro', null);
                createWq.mutate();
              }}
            >
              {createWq.isPending ? 'Creating…' : 'Create account'}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">(org not linked)</span>
          )}
          {msg.wappro && <span className="text-xs text-muted-foreground">{msg.wappro}</span>}
        </div>
      </div>

      {/* WapDocs */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-20 text-xs font-medium text-muted-foreground">WapDocs</span>
          <select
            aria-label="WapDocs role"
            value={wdRole}
            onChange={(e) => setWdRole(e.target.value as WapdocsRole)}
            className={selectCls}
          >
            <option value="USER">User</option>
            <option value="ORG_ADMIN">Org admin</option>
          </select>
          {wd ? (
            <button
              type="button"
              className={applyBtn}
              disabled={applyWd.isPending}
              onClick={() => {
                setAppMsg('wapdocs', null);
                applyWd.mutate();
              }}
            >
              {applyWd.isPending ? 'Applying…' : 'Apply'}
            </button>
          ) : hasWapdocs ? (
            <button
              type="button"
              className={applyBtn}
              disabled={createWd.isPending}
              onClick={() => {
                setAppMsg('wapdocs', null);
                createWd.mutate();
              }}
            >
              {createWd.isPending ? 'Creating…' : 'Create account'}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">(org not linked)</span>
          )}
          {msg.wapdocs && <span className="text-xs text-muted-foreground">{msg.wapdocs}</span>}
        </div>
      </div>
    </div>
  );
}
